/**
 * Public Booking V2 Page
 *
 * Route: /book/c/:clinicSlug/t/:typeSlug
 *
 * Same split-screen Calendly-style layout as the V1 page, driven by the
 * V2 step machine. See usePublicBookingV2 for the flow.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { BookingSummary } from '@/components/booking';
import {
  SelectDateStep,
  SelectTimeStep,
  ConsentCheckStep,
  BookingConfirmationView,
} from '@/components/booking/public';
import { PaymentStep } from '@/components/booking/public/PaymentStep';
import {
  SelectPractitionerStep,
  EnterEmailStep,
  VerifyOtpStep,
  ConfirmDobStep,
  OnboardingFormStep,
  AmbiguousMatchStep,
} from '@/components/booking/public/steps/v2';
import { usePublicBookingV2 } from '@/components/booking/public/usePublicBookingV2';

const PublicBookingV2Page: React.FC = () => {
  const booking = usePublicBookingV2();

  const providerFullName = booking.selectedPractitioner?.full_name || '';
  const providerDisplayName = providerFullName ? `Dr. ${providerFullName}` : '';

  if (booking.step === 'loading') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        <div className='text-center'>
          <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-teal-600' />
          <p className='text-slate-500'>Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (booking.step === 'error') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 p-4'>
        <Card className='w-full max-w-md shadow-lg'>
          <CardContent className='pt-8 text-center'>
            <AlertCircle className='mx-auto mb-4 h-16 w-16 text-red-500' />
            <h1 className='mb-2 text-2xl font-bold text-slate-900'>Booking Unavailable</h1>
            <p className='text-slate-600'>
              {booking.error || 'This booking page is not available.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (booking.step === 'confirmation' && booking.confirmation) {
    // Map V2's lean confirmation onto the existing confirmation view shape.
    const adapted = {
      appointment: booking.confirmation.appointment,
      practitioner: {
        name: providerDisplayName,
        credentials: booking.selectedPractitioner?.credentials,
      },
      message: 'Your appointment has been booked successfully!',
    };
    return (
      <BookingConfirmationView
        confirmation={adapted}
        clinicName={booking.pageData?.clinic.name}
        intakeLink={booking.confirmation.intake?.intakeLink || null}
      />
    );
  }

  // Back button is meaningful on every step except the auto-routed terminals.
  // Hide back on consent step too — ConsentCheckStep already provides its
  // own cancel affordance via onCancel.
  const showBackButton = !['loading', 'error', 'confirmation', 'select-practitioner', 'consent-check'].includes(
    booking.step,
  );

  const summaryProps = {
    practitionerName: providerDisplayName,
    practitionerCredentials: booking.selectedPractitioner?.credentials,
    practitionerSpecialization: booking.selectedPractitioner?.specialization,
    clinicName: booking.pageData?.clinic.name,
    appointmentTypeName: booking.pageData?.appointment_type.name,
    durationMinutes: booking.pageData?.appointment_type.duration_minutes,
    priceCents: booking.pageData?.appointment_type.price_cents,
    isTelehealth: booking.pageData?.appointment_type.telehealth_enabled,
    selectedDate: booking.selectedDate,
    selectedTime: booking.selectedSlot
      ? booking.formatSlotTime(booking.selectedSlot, booking.providerTimezone || '')
      : undefined,
    timezone: booking.visitorTimezone,
    onTimezoneChange: booking.setVisitorTimezone,
    showTimezoneSelector: ['select-date', 'select-time', 'enter-email'].includes(booking.step),
  };

  return (
    <div className='min-h-screen bg-white'>
      <div className='flex min-h-screen flex-col lg:flex-row'>
        {/* Left panel — practitioner / appointment summary */}
        <div className='hidden flex-shrink-0 border-r border-slate-200 bg-slate-50/80 lg:block lg:w-[280px] xl:w-[320px]'>
          <div className='sticky top-0 p-5 xl:p-6'>
            <BookingSummary {...summaryProps} />
          </div>
        </div>

        {/* Right panel — booking flow */}
        <div className='flex-1 p-4 sm:p-6 lg:p-10 xl:p-12'>
          <div
            className={
              booking.step === 'onboarding-form'
                ? 'mx-auto max-w-3xl'
                : 'mx-auto max-w-xl'
            }
          >
            {/* Mobile summary */}
            <div className='mb-5 lg:hidden'>
              <BookingSummary {...summaryProps} compact />
            </div>

            {showBackButton && (
              <Button
                variant='ghost'
                size='sm'
                onClick={booking.handleBack}
                className='-ml-2 mb-4 text-slate-600 hover:text-slate-900'
              >
                <ArrowLeft className='mr-1 h-4 w-4' />
                Back
              </Button>
            )}

            {booking.step === 'select-practitioner' && booking.pageData && (
              <SelectPractitionerStep
                practitioners={booking.pageData.practitioners}
                onSelect={booking.handleSelectPractitioner}
              />
            )}

            {booking.step === 'select-date' && (
              <SelectDateStep
                selectedDate={booking.selectedDate}
                onSelectDate={booking.handleSelectDate}
                availableDates={booking.availableDates}
                currentMonth={booking.currentMonth}
                onMonthChange={booking.setCurrentMonth}
                loading={booking.loadingDates}
              />
            )}

            {booking.step === 'select-time' && booking.selectedDate && (
              <SelectTimeStep
                selectedDate={booking.selectedDate}
                slots={booking.availableSlots}
                selectedSlot={booking.selectedSlot?.start}
                onSelectSlot={booking.handleSelectSlot}
                loading={booking.loadingSlots}
                formatSlotTime={booking.formatSlotTime}
                providerTimezone={booking.providerTimezone || ''}
              />
            )}

            {booking.step === 'enter-email' && (
              <EnterEmailStep
                initialEmail={booking.email}
                onSubmit={booking.handleRequestOtp}
                loading={booking.loadingAction}
                error={booking.actionError}
              />
            )}

            {booking.step === 'verify-otp' && (
              <VerifyOtpStep
                email={booking.email}
                onSubmit={booking.handleVerifyOtp}
                onResend={booking.handleResendOtp}
                onChangeEmail={booking.handleChangeEmail}
                loading={booking.loadingAction}
                error={booking.actionError}
              />
            )}

            {booking.step === 'confirm-dob' && booking.patientPreview && (
              <ConfirmDobStep
                patientPreview={booking.patientPreview}
                onSubmit={booking.handleConfirmDob}
                loading={booking.loadingAction}
                error={booking.actionError}
                attemptsRemaining={booking.dobAttemptsRemaining}
              />
            )}

            {booking.step === 'onboarding-form' && booking.pageData && (
              <OnboardingFormStep
                clinicName={booking.pageData.clinic.name}
                verifiedEmail={booking.email}
                onSubmit={booking.handleOnboardAndConfirm}
                isSubmitting={booking.loadingAction}
              />
            )}

            {booking.step === 'consent-check'
              && booking.pageData
              && booking.boundPatientId && (
              <>
                <ConsentCheckStep
                  clinicId={booking.pageData.clinic.id}
                  patientId={booking.boundPatientId}
                  patientName={booking.boundPatientName || ''}
                  onComplete={booking.handleConsentComplete}
                  onCancel={booking.handleConsentCancel}
                />
                {booking.loadingAction && (
                  <div className='mt-6 flex items-center gap-2 text-sm text-slate-500'>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600' />
                    Finalizing your booking…
                  </div>
                )}
                {booking.actionError && !booking.loadingAction && (
                  <div className='mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>
                    <p className='mb-3'>{booking.actionError}</p>
                    <Button
                      type='button'
                      size='sm'
                      variant='outline'
                      onClick={() => { void booking.handleConsentComplete(); }}
                    >
                      Try again
                    </Button>
                  </div>
                )}
              </>
            )}

            {booking.step === 'payment' && booking.sessionToken && (
              <PaymentStep
                sessionToken={booking.sessionToken}
                priceCents={booking.pageData?.appointment_type.price_cents || 0}
                stripeAccountId={booking.pageData?.clinic.stripe_account_id || ''}
                onComplete={booking.handlePaymentComplete}
                onSkip={() => booking.handlePaymentComplete('')}
                externalError={booking.actionError}
                isSubmitting={booking.loadingAction}
              />
            )}

            {booking.step === 'ambiguous' && (
              <AmbiguousMatchStep clinicName={booking.pageData?.clinic.name} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingV2Page;
